import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { 
  Home, ArrowUpCircle, ArrowLeftRight, Send, CreditCard, Users, History, 
  Shield, Settings, QrCode, Link2, Code, Grid3X3, Award, HelpCircle, 
  GraduationCap, LogOut, ChevronLeft, Search, Download, ChevronDown,
  Calendar, Check, Bell, Copy, ChevronRight
} from "lucide-react";

// Generate random values outside component
const generateRandomId = () => Math.floor(100000 + Math.random() * 900000);
const generateBrowserTheme = () => 'dark'; // Default dark theme

export default function CashinoutPaymentsPreview() {
  const [searchParams] = useSearchParams();
  const pageParam = searchParams.get('page');
  const [currentPage, setCurrentPage] = useState(pageParam ? parseInt(pageParam) || 1 : 1);
  const [data, setData] = useState(null);
  const [menuCollapsed, setMenuCollapsed] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [browserTheme, setBrowserTheme] = useState(generateBrowserTheme);
  const [showBrowser, setShowBrowser] = useState(true);
  const [randomUserId] = useState(generateRandomId);

  // Set browser title and favicon
  useEffect(() => {
    document.title = 'История Платежей';
    
    // Check for theme in URL params
    const urlParams = new URLSearchParams(window.location.search);
    const themeParam = urlParams.get('theme');
    if (themeParam === 'dark' || themeParam === 'light') {
      setBrowserTheme(themeParam);
    }
    
    // Set favicon
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      document.head.appendChild(favicon);
    }
    favicon.href = '/cashinout-favicon.ico';
    
    return () => {
      document.title = 'CRM Platform';
    };
  }, []);

  useEffect(() => {
    const dataParam = searchParams.get('data');
    if (dataParam) {
      try {
        const parsedData = JSON.parse(decodeURIComponent(dataParam));
        setData(parsedData);
        
        // Read showBrowser from data
        if (parsedData.showBrowser !== undefined) {
          setShowBrowser(parsedData.showBrowser);
        }
        
        // Read browserTheme from data
        if (parsedData.browserTheme) {
          setBrowserTheme(parsedData.browserTheme);
        }
        
        // Read page from URL params
        const pageP = searchParams.get('page');
        if (pageP) {
          setCurrentPage(parseInt(pageP) || 1);
        }
        
        // Read typeFilter from URL params
        const typeP = searchParams.get('typeFilter');
        if (typeP && ['all', 'deposit', 'withdrawal'].includes(typeP)) {
          setTypeFilter(typeP);
        }
        
        // Read defaultTypeFilter from parsed data
        if (dataParam) {
          try {
            const pd = JSON.parse(decodeURIComponent(dataParam));
            if (pd.defaultTypeFilter && ['all', 'deposit', 'withdrawal'].includes(pd.defaultTypeFilter)) {
              setTypeFilter(pd.defaultTypeFilter);
            }
          } catch(e) {}
        }
        
        // Auto-set date filter to first and last transaction dates (for input type="date" format YYYY-MM-DD)
        if (parsedData.transactions && parsedData.transactions.length > 0) {
          const dates = parsedData.transactions
            .map(tx => {
              if (!tx.date) return null;
              const parts = tx.date.split('.');
              if (parts.length === 3) {
                return {
                  sortKey: new Date(parts[2], parts[1]-1, parts[0]).getTime(),
                  isoDate: `${parts[2]}-${parts[1]}-${parts[0]}`, // YYYY-MM-DD for input
                  displayDate: tx.date // DD.MM.YYYY
                };
              }
              return null;
            })
            .filter(d => d)
            .sort((a, b) => a.sortKey - b.sortKey);
          
          if (dates.length > 0) {
            setDateFrom(dates[0].displayDate); // Oldest date (first deposit if random ops enabled)
            setDateTo(dates[dates.length - 1].displayDate); // Newest date
          }
        }
      } catch (e) {
        console.error("Error parsing data:", e);
      }
    }
  }, [searchParams]);

  if (!data) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center">
        <div className="text-gray-600">Загрузка...</div>
      </div>
    );
  }

  const allTransactions = data.transactions || [];
  const accountName = `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Пользователь';
  const userId = data.cashinoutId || randomUserId;

  // Parse date string "DD.MM.YYYY" to Date object
  const parseDate = (dateStr) => {
    if (!dateStr) return null;
    const parts = dateStr.split('.');
    if (parts.length === 3) {
      return new Date(parts[2], parts[1] - 1, parts[0]);
    }
    return null;
  };

  // Filter transactions by date range and type
  let filteredTransactions = allTransactions.filter(tx => {
    // Type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'deposit' && tx.type !== 'deposit') return false;
      if (typeFilter === 'withdrawal' && tx.type !== 'withdrawal') return false;
    }
    
    // Date filter
    if (!dateFrom && !dateTo) return true;
    
    const txDate = parseDate(tx.date);
    if (!txDate) return true;
    
    if (dateFrom) {
      const fromDate = parseDate(dateFrom);
      if (fromDate) {
        fromDate.setHours(0, 0, 0, 0);
        if (txDate < fromDate) return false;
      }
    }
    
    if (dateTo) {
      const toDate = parseDate(dateTo);
      if (toDate) {
        toDate.setHours(23, 59, 59, 999);
        if (txDate > toDate) return false;
      }
    }
    
    return true;
  });
  
  // Sort transactions by date and time (newest first - descending order)
  filteredTransactions.sort((a, b) => {
    const parseDateTime = (dateStr, timeStr) => {
      if (!dateStr) return 0;
      const parts = dateStr.split('.');
      if (parts.length === 3) {
        const [day, month, year] = parts;
        const time = timeStr || '00:00';
        return new Date(`${year}-${month}-${day}T${time}`).getTime();
      }
      return 0;
    };
    return parseDateTime(b.date, b.time) - parseDateTime(a.date, a.time);
  });
  
  const itemsPerPage = 10;
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const transactions = filteredTransactions.slice(startIndex, startIndex + itemsPerPage);

  // Get type filter label for dropdown button
  const getTypeFilterLabel = () => {
    if (typeFilter === 'deposit') return 'Пополнение';
    if (typeFilter === 'withdrawal') return 'Вывод';
    return 'Все';
  };

  // Get date range label
  const getDateRangeLabel = () => {
    if (!dateFrom && !dateTo) return 'Все время';
    if (dateFrom && dateTo) {
      // dateFrom and dateTo are already in DD.MM.YYYY format
      return `${dateFrom} - ${dateTo}`;
    }
    if (dateFrom) return `с ${dateFrom}`;
    if (dateTo) return `до ${dateTo}`;
    return 'Все время';
  };

  // Clear date filters
  const clearDateFilter = () => {
    setDateFrom('');
    setDateTo('');
    setShowDatePicker(false);
  };

  const menuItems = [
    { icon: Home, label: 'Главная', active: false },
    { icon: ArrowUpCircle, label: 'Пополнить', active: false },
    { icon: ArrowLeftRight, label: 'Обмен', active: false },
    { icon: Send, label: 'Отправить', active: false },
    { icon: CreditCard, label: 'Виртуальные карты', active: false },
    { icon: Users, label: 'Реферальная программа', active: false },
    { icon: History, label: 'История', active: true },
    { icon: Shield, label: '2FA', active: false },
    { icon: Settings, label: 'Настройки', active: false },
  ];

  const appItems = [
    { icon: QrCode, label: 'QR для авторизации' },
  ];

  const businessItems = [
    { icon: Link2, label: 'Платёжные ссылки' },
    { icon: Code, label: 'API' },
    { icon: Grid3X3, label: 'Массовые выплаты' },
  ];

  const bottomItems = [
    { icon: Award, label: 'Тарифы' },
    { icon: HelpCircle, label: 'Служба поддержки' },
    { icon: GraduationCap, label: 'Академия Cashinout' },
    { icon: HelpCircle, label: 'Частые вопросы' },
    { icon: LogOut, label: 'Выйти' },
  ];

  // Get type label
  const getTypeLabel = (tx) => {
    if (tx.type === 'deposit') return 'Пополнение';
    if (tx.type === 'withdrawal') return 'Вывод';
    return 'Перевод';
  };

  // Theme colors
  const isDark = browserTheme === 'dark';
  const theme = {
    bg: isDark ? '#202124' : '#dee1e6',
    tabBarBg: isDark ? '#202124' : '#dee1e6',
    activeTabBg: isDark ? '#292a2d' : '#ffffff',
    inactiveTabBg: isDark ? '#35363a' : '#c9cdd4',
    addressBarBg: isDark ? '#292a2d' : '#ffffff',
    addressInputBg: isDark ? '#202124' : '#f1f3f4',
    textPrimary: isDark ? '#e8eaed' : '#202124',
    textSecondary: isDark ? '#9aa0a6' : '#5f6368',
    hoverBg: isDark ? '#3c3d41' : '#e8eaed',
    border: isDark ? '#3c4043' : '#dadce0',
  };
  // Set body background to match theme to avoid dark strips in screenshots  useEffect(() => {    document.documentElement.style.backgroundColor = theme.bg;    document.body.style.backgroundColor = theme.bg;    document.body.style.margin = "0";    document.body.style.padding = "0";    return () => {      document.documentElement.style.backgroundColor = "";      document.body.style.backgroundColor = "";    };  }, [theme.bg]);

  // Browser frame component
  const BrowserFrame = () => (
    <div style={{ backgroundColor: theme.tabBarBg }} className="flex-shrink-0">
      {/* Title Bar with Tabs and Window Controls */}
      <div className="flex items-center h-[38px]" style={{ backgroundColor: theme.tabBarBg }}>
        {/* Tabs Area */}
        <div className="flex items-end flex-1 pl-2">
          {/* Tab 1 - Ozon Карта (INACTIVE) */}
          <div 
            className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer"
            style={{ backgroundColor: theme.inactiveTabBg }}
          >
            <img src="/ozon-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textSecondary }}>Ozon Карта</span>
            <button className="w-5 h-5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100" style={{ color: theme.textSecondary }}>
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          
          {/* Tab 2 - История Платежей (ACTIVE) */}
          <div 
            className="flex items-center h-[34px] px-3 rounded-t-lg min-w-[140px] max-w-[200px] mr-[1px] group cursor-pointer"
            style={{ backgroundColor: theme.activeTabBg }}
          >
            <img src="/cashinout-favicon.ico" alt="" className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="text-[13px] truncate flex-1" style={{ color: theme.textPrimary }}>История Платежей</span>
            <button className="w-5 h-5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100" style={{ color: theme.textSecondary }}>
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
          </div>
          
          {/* New Tab Button */}
          <button className="w-7 h-7 flex items-center justify-center rounded-full ml-1 mb-1" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
            </svg>
          </button>
        </div>
        
        {/* Windows Controls (right side) */}
        <div className="flex items-center h-full">
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}>
            <svg className="w-[10px] h-[1px]" viewBox="0 0 10 1"><rect fill="currentColor" width="10" height="1"/></svg>
          </button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#3c3d41]" style={{ color: theme.textSecondary }}>
            <svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="1"><rect x="0.5" y="0.5" width="9" height="9"/></svg>
          </button>
          <button className="w-[46px] h-full flex items-center justify-center hover:bg-[#e81123] hover:text-white" style={{ color: theme.textSecondary }}>
            <svg className="w-[10px] h-[10px]" viewBox="0 0 10 10" stroke="currentColor" strokeWidth="1.2"><line x1="0" y1="0" x2="10" y2="10"/><line x1="10" y1="0" x2="0" y2="10"/></svg>
          </button>
        </div>
      </div>
      
      {/* Address Bar Row */}
      <div className="flex items-center h-[40px] px-2" style={{ backgroundColor: theme.addressBarBg }}>
        <div className="flex items-center gap-1 mr-2">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: isDark ? '#5f6368' : '#dadce0' }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg>
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z"/></svg>
          </button>
        </div>
        <div className="flex-1 flex items-center h-[32px] px-4 rounded-full" style={{ backgroundColor: theme.addressInputBg, border: `1px solid ${theme.border}` }}>
          <svg className="w-4 h-4 mr-3 flex-shrink-0" style={{ color: theme.textSecondary }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
          </svg>
          <span className="text-[14px]" style={{ color: theme.textPrimary }}>cashinout.io/history</span>
        </div>
        <div className="flex items-center gap-1 ml-3">
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
          </button>
          <button className="w-8 h-8 flex items-center justify-center rounded-full" style={{ color: theme.textSecondary }}>
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="2"/><circle cx="12" cy="5" r="2"/><circle cx="12" cy="19" r="2"/></svg>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col template-preview-scale" style={{ backgroundColor: showBrowser ? theme.bg : '#f5f7fa', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      {/* Google Chrome Browser Header - Only show if showBrowser is true */}
      {showBrowser && <BrowserFrame />}

      {/* Main Content (Website) */}
      <div className="flex-1 flex bg-[#f5f7fa]">
        {/* Sidebar */}
        <aside className={`${menuCollapsed ? 'w-16' : 'w-56'} bg-[#303646] flex flex-col transition-all duration-300 flex-shrink-0`}>
        {/* Logo */}
        <div className="p-4 flex items-center gap-3">
          <img 
            src="/cashinout-logo.png" 
            alt="Cashinout" 
            className={`${menuCollapsed ? 'w-8' : 'h-8'} object-contain`}
          />
        </div>

        {/* Main Menu */}
        <nav className="flex-1 py-2 overflow-y-auto">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                  item.active 
                    ? 'text-white bg-[#3d4557]' 
                    : 'text-[#9ca3af] hover:bg-[#3d4557] hover:text-white'
                }`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* App Section */}
          {!menuCollapsed && (
            <div className="px-4 py-2 mt-4">
              <span className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Приложение</span>
            </div>
          )}
          {appItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* For Business Section */}
          {!menuCollapsed && (
            <div className="px-4 py-2 mt-4">
              <span className="text-xs font-medium text-[#6b7280] uppercase tracking-wide">Для бизнеса</span>
            </div>
          )}
          {businessItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <a
                key={index}
                href="#"
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!menuCollapsed && <span>{item.label}</span>}
              </a>
            );
          })}

          {/* Bottom Items */}
          <div className="mt-4 border-t border-[#3d4557] pt-2">
            {bottomItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <a
                  key={index}
                  href="#"
                  className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#9ca3af] hover:bg-[#3d4557] hover:text-white transition-colors"
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!menuCollapsed && <span>{item.label}</span>}
                </a>
              );
            })}
          </div>
        </nav>

        {/* Hide Menu Toggle */}
        <button 
          onClick={() => setMenuCollapsed(!menuCollapsed)}
          className="flex items-center gap-3 px-4 py-3 text-sm text-[#9ca3af] hover:bg-[#3d4557] border-t border-[#3d4557]"
        >
          <div className={`w-5 h-3 rounded-full bg-[#4a5568] flex items-center px-0.5 transition-all ${menuCollapsed ? 'justify-end' : 'justify-start'}`}>
            <div className="w-2 h-2 rounded-full bg-white"></div>
          </div>
          {!menuCollapsed && <span>Скрыть меню</span>}
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-end gap-4">
          {/* Language */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="text-lg">🇷🇺</span>
            <ChevronLeft className="w-4 h-4 rotate-[-90deg]" />
          </div>
          
          {/* User Name */}
          <span className="text-sm text-gray-700 font-medium">{accountName}</span>
          
          {/* User Avatar */}
          <div className="w-8 h-8 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center">
            <span className="text-white text-xs font-medium">
              {accountName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </span>
          </div>
          
          {/* ID */}
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <span>ID:</span>
            <span className="font-medium">{userId}</span>
          </div>
          
          {/* Icons */}
          <button className="p-2 text-gray-500 hover:text-gray-700">
            <Copy className="w-5 h-5" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 relative">
            <Bell className="w-5 h-5" />
            <span className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full"></span>
          </button>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-5xl">
            <h1 className="text-2xl font-semibold text-gray-900 mb-6">История</h1>

            {/* Filters Row */}
            <div className="flex items-center gap-4 mb-6">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Поиск"
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white"
                />
              </div>

              {/* All Dropdown */}
              <div className="relative">
                <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                  <span className="text-gray-900">Все</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Export Button */}
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                <span className="text-gray-900">Экспорт</span>
                <Download className="w-4 h-4 text-gray-400" />
              </button>
            </div>

            {/* Filter Dropdowns */}
            <div className="flex items-center gap-4 mb-6">
              {/* Currency */}
              <div className="flex-1">
                <label className="block text-xs text-gray-500 mb-1">Валюта</label>
                <button className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white">
                  <span className="text-gray-900">RUB</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              {/* Type Filter */}
              <div className="flex-1 relative">
                <label className="block text-xs text-gray-500 mb-1">Тип</label>
                <button 
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white"
                >
                  <span className="text-gray-900">{getTypeFilterLabel()}</span>
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                </button>
                
                {/* Type Dropdown */}
                {showTypeDropdown && (
                  <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <button
                      onClick={() => { setTypeFilter('all'); setShowTypeDropdown(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'all' ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    >
                      Все
                    </button>
                    <button
                      onClick={() => { setTypeFilter('deposit'); setShowTypeDropdown(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'deposit' ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    >
                      Пополнение
                    </button>
                    <button
                      onClick={() => { setTypeFilter('withdrawal'); setShowTypeDropdown(false); }}
                      className={`w-full px-4 py-2.5 text-left text-sm hover:bg-gray-50 ${typeFilter === 'withdrawal' ? 'bg-blue-50 text-blue-600' : 'text-gray-900'}`}
                    >
                      Вывод
                    </button>
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div className="flex-1 relative">
                <label className="block text-xs text-gray-500 mb-1">Дата</label>
                <button 
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="w-full flex items-center justify-between px-4 py-2.5 border border-gray-200 rounded-lg text-sm hover:bg-gray-50 bg-white"
                >
                  <span className="text-gray-900 truncate">{getDateRangeLabel()}</span>
                  <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                </button>
                
                {/* Date Picker Dropdown */}
                {showDatePicker && (
                  <div className="absolute top-full left-0 mt-1 w-72 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4">
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">С даты (ДД.ММ.ГГГГ)</label>
                        <input
                          type="text"
                          placeholder="01.01.2026"
                          value={dateFrom}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^\d.]/g, '');
                            // Auto-format: add dots after day and month
                            if (value.length === 2 && !value.includes('.')) {
                              value = value + '.';
                            } else if (value.length === 5 && value.split('.').length === 2) {
                              value = value + '.';
                            }
                            if (value.length <= 10) {
                              setDateFrom(value);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">По дату (ДД.ММ.ГГГГ)</label>
                        <input
                          type="text"
                          placeholder="31.01.2026"
                          value={dateTo}
                          onChange={(e) => {
                            let value = e.target.value.replace(/[^\d.]/g, '');
                            // Auto-format: add dots after day and month
                            if (value.length === 2 && !value.includes('.')) {
                              value = value + '.';
                            } else if (value.length === 5 && value.split('.').length === 2) {
                              value = value + '.';
                            }
                            if (value.length <= 10) {
                              setDateTo(value);
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-500"
                        />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={clearDateFilter}
                          className="flex-1 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                        >
                          Сбросить
                        </button>
                        <button
                          onClick={() => setShowDatePicker(false)}
                          className="flex-1 px-3 py-2 text-sm text-white bg-blue-500 rounded-lg hover:bg-blue-600"
                        >
                          Применить
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Table */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Статус</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Время</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Валюта</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Сумма</th>
                    <th className="text-left text-sm font-medium text-gray-500 px-4 py-3">Тип</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-12 text-center text-gray-400">
                        {(dateFrom || dateTo) && allTransactions.length > 0 
                          ? 'Нет операций за выбранный период' 
                          : 'Список операций пуст'}
                      </td>
                    </tr>
                  ) : (
                    transactions.map((tx, index) => {
                      const isDeposit = tx.type === 'deposit';
                      const amount = tx.amount || 0;
                      
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                                <Check className="w-3.5 h-3.5 text-green-500" />
                              </div>
                              <span className="text-sm text-green-500">Успешно</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="text-sm text-gray-900">{tx.date}</div>
                            <div className="text-xs text-gray-400">{tx.time || '00:00'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">RUB</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-sm font-medium ${isDeposit ? 'text-green-500' : 'text-red-500'}`}>
                              {isDeposit ? '+' : '-'}{amount.toLocaleString('ru-RU')} ₽
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-sm text-gray-900">{getTypeLabel(tx)}</span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-white">
                  <div className="text-sm text-gray-500">
                    Страница {currentPage} из {totalPages} ({filteredTransactions.length} операций)
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1.5 rounded text-sm ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
                    >
                      &larr;
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 rounded text-sm font-medium ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1.5 rounded text-sm ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-blue-600 hover:bg-blue-50'}`}
                    >
                      &rarr;
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
  );
}
